import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";

import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";

import Topbar from "./Topbar";
import AppDrawer from "./AppDrawer";
import { getConfigs, getEnums, getUserProfile } from "../controllers/RuleController";
import { useDispatch, useSelector } from "react-redux";
import { setConfigs, setEnums, setTimezone, setUserRoles } from "../redux/globalDataSlice";
import {
    Backdrop,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
} from "@mui/material";
import TimezoneSelect from "react-timezone-select";
import { timezoneOptions } from "../utils/dateTime";
import Message from "../components/Message";

const drawerWidth = 260;
const collapsedWidth = 80;

export default function AppLayout() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const [loading, setLoading] = useState(false);
    const [pendingTimezone, setPendingTimezone] = useState(null);
    const [apiMessage, setApiMessage] = useState({
        open: false,
        type: "success",
        message: "",
    });

    const currentDrawerWidth = collapsed ? collapsedWidth : drawerWidth;

    const handleDrawerToggleMobile = () => setMobileOpen((prev) => !prev);
    const handleDrawerToggleDesktop = () => setCollapsed((prev) => !prev);
    const dispatch = useDispatch();
    const timezone = useSelector((state) => state.globalData.timezone);

    const handleConfirmTimezone = () => {
        if (!pendingTimezone?.value) return;
        window.localStorage.setItem("selectedTimezone", pendingTimezone.value);
        dispatch(setTimezone(pendingTimezone.value));
    };

    const getRuleEnums = async () => {
        try {
            setLoading(true);
            const res = await getEnums();
            if (res) dispatch(setEnums(res));
            setLoading(false);
        } catch (e) {
            console.log("Failed to fetch enums", e);
            setLoading(false);
        }
    };

    const getRuleConfigs = async () => {
        try {
            setLoading(true);
            const res = await getConfigs();
            if (res) dispatch(setConfigs(res));
            setLoading(false);
        } catch (e) {
            console.log("Failed to fetch configs", e);
            setLoading(false);
        }
    };

    const getUserProfileData = async () => {
        try {
            setLoading(true);
            const res = await getUserProfile();
            if (res) dispatch(setUserRoles(res.roles));
            setLoading(false);
        } catch (e) {
            setApiMessage({
                open: true,
                type: "error",
                message: e && e.message ? e.message : "Failed to load user profile",
            });
            setLoading(false);
        }
    }

    useEffect(() => {
        getRuleEnums();
        getRuleConfigs();
        getUserProfileData();
    }, [])


    return (
        <Box sx={{ display: "flex" }}>
            <Backdrop
                open={loading}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 999,
                    color: "#fff",
                    backdropFilter: "blur(3px)",
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <CssBaseline />

            {/* ✅ Topbar FULL WIDTH */}
            <Topbar
                onMobileMenuClick={handleDrawerToggleMobile}
                onDesktopToggleClick={handleDrawerToggleDesktop}
                collapsed={collapsed}
            />

            {/* Drawer (below topbar) */}
            <AppDrawer
                drawerWidth={currentDrawerWidth}
                collapsed={collapsed}
                mobileOpen={mobileOpen}
                onClose={handleDrawerToggleMobile}
            />

            {/* Main content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    mt: 8, // push below AppBar
                    width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
                    background: "#f5f6fa",
                    minHeight: "100vh",
                }}
            >
                <Outlet />
            </Box>

            <Dialog open={timezone === null} disableEscapeKeyDown>
                <DialogTitle sx={{ fontWeight: 700 }}>Select Your Timezone</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Choose the timezone used to display all dates and times across the app.
                    </Typography>
                    <TimezoneSelect
                        value={pendingTimezone}
                        onChange={setPendingTimezone}
                        placeholder="Select timezone..."
                        menuPortalTarget={document.body}
                        timezones={timezoneOptions}
                        styles={{
                            control: (base) => ({
                                ...base,
                                fontSize: "0.875rem",
                                borderColor: "#c4c4c4",
                                boxShadow: "none",
                                "&:hover": { borderColor: "#111827" },
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            singleValue: (base) => ({ ...base, color: "#111827" }),
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        disabled={!pendingTimezone}
                        onClick={handleConfirmTimezone}
                        fullWidth
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
            <Message
                open={apiMessage.open}
                type={apiMessage.type}
                message={apiMessage.message}
                onClose={() => setApiMessage((prev) => ({ ...prev, open: false }))}
            />
        </Box>
    );
}
